import React, { useContext } from 'react';
import './SimpleRuneDisplay.css';
import { ShardNames } from '../../../../../BuildOverview/runeConfig';
import GameAssetsContext from '../../../../../context/GameAssetContext';
import { SimplifiedChampion, SimplifiedRunePage } from '../../../../../gameTypes';
import { runeIdMapping as sharedRuneIdMapping, runeIdToStyleName } from '../../../../../../NewWebSocket/utils/runeIdMapping';
import { RUNE_TREE_IMAGES, SHARD_IMAGES } from '../../../../../../NewWebSocket/utils/runeConfig';
import { getRuneImageUrlById } from '../../../../../../utils/runeImageMapping';

interface SimpleRuneDisplayProps {
    primaryRunes: string[];
    secondaryRunes: string[];
    shards: ShardNames[];
    championId: string;
    runePage: SimplifiedRunePage;
    champion: SimplifiedChampion;
}

const SimpleRuneDisplay: React.FC<SimpleRuneDisplayProps> = ({ 
    primaryRunes, 
    secondaryRunes, 
    shards, 
    championId, 
    runePage, 
    champion 
}) => {
    const { gameWideAssets } = useContext(GameAssetsContext);

    // Get image URLs helper functions - updated to use shared utility
    const getRuneImageUrl = (runeId: string): string => {
        const numericRuneId = parseInt(runeId);
        return getRuneImageUrlById(
            numericRuneId, 
            sharedRuneIdMapping, 
            gameWideAssets.runeIcons || {}, 
            gameWideAssets.runes?.[runeId]
        );
    };

    const getTreeImageUrl = (styleId: string): string => {
        // Map the numeric ID to the style name and use the shared RUNE_TREE_IMAGES
        const styleName = runeIdToStyleName[styleId];
        if (styleName && RUNE_TREE_IMAGES[styleName]) {
            return RUNE_TREE_IMAGES[styleName];
        }
        // Fallback to gameWideAssets
        return gameWideAssets.runeTrees[styleId] || '';
    };

    // Map shard IDs to names used in SHARD_IMAGES if needed
    const mapShardIdToName = (shardId: string): string => {
        // Check if it's already a name like 'adaptiveForce'
        if (SHARD_IMAGES[shardId]) {
            return shardId;
        }

        // Map numeric IDs to names
        const numericId = parseInt(shardId);
        if (!isNaN(numericId)) {
            switch (numericId) {
                case 5008: return 'adaptiveForce';
                case 5005: return 'attackSpeed';
                case 5007: return 'abilityHaste';
                case 5010: return 'moveSpeed';
                case 5001: return 'healthScaling';
                case 5011: return 'health';
                case 5013: return 'tenacity';
                case 5002: return 'armor';
                case 5003: return 'magicResist';
            }
        }
        
        // Handle other format conversions if needed
        if (shardId.toLowerCase().includes('adaptive')) return 'adaptiveForce';
        if (shardId.toLowerCase().includes('attack')) return 'attackSpeed';
        if (shardId.toLowerCase().includes('ability') || shardId.toLowerCase().includes('haste')) return 'abilityHaste';
        if (shardId.toLowerCase().includes('move')) return 'moveSpeed';
        if (shardId.toLowerCase().includes('scaling')) return 'healthScaling';
        if (shardId.toLowerCase().includes('health') && !shardId.toLowerCase().includes('scaling')) return 'health';
        if (shardId.toLowerCase().includes('tenacity')) return 'tenacity';
        if (shardId.toLowerCase().includes('armor')) return 'armor';
        if (shardId.toLowerCase().includes('magic')) return 'magicResist';
        
        return shardId;
    };

    const getShardImageUrl = (shardId: string): string => {
        // Try numeric mapping first
        const numericShardId = parseInt(shardId);
        if (!isNaN(numericShardId) && sharedRuneIdMapping[numericShardId]?.path) {
            return `https://ddragon.leagueoflegends.com/cdn/img/perk-images/${sharedRuneIdMapping[numericShardId].path}`;
        }

        // Try to map to SHARD_IMAGES keys
        const mappedShardName = mapShardIdToName(shardId);
        if (SHARD_IMAGES[mappedShardName]) {
            return SHARD_IMAGES[mappedShardName];
        }
        
        // Final fallback to gameWideAssets
        return gameWideAssets.shards[shardId] || '';
    };

    const renderTreeImage = (treeId: string) => {
        const treeImageUrl = getTreeImageUrl(treeId);
        const numericTreeId = parseInt(treeId);
        return treeImageUrl ? (
            <img src={treeImageUrl} alt={`${!isNaN(numericTreeId) ? sharedRuneIdMapping[numericTreeId]?.name : ''} Tree`} className="sr-tree-image" />
        ) : null;
    };

    const renderRunes = (runeList: string[]) => {
        return runeList.slice(1).map((rune, index) => {
            const imageUrl = getRuneImageUrl(rune);
            const runeClass = index === 0 && runeList === primaryRunes ? 'sr-rune-icon sr-keystone' : 'sr-rune-icon';
            const numericRuneId = parseInt(rune);
            return (
                <div key={`rune-${index}`} className={runeClass}>
                    <img src={imageUrl} alt={!isNaN(numericRuneId) ? sharedRuneIdMapping[numericRuneId]?.name || "Rune" : "Rune"} />
                </div>
            );
        });
    };

    const renderShards = () => {
        return shards.map((shard, index) => {
            const imageUrl = getShardImageUrl(shard);
            const numericShardId = parseInt(shard);
            const mappedShardName = mapShardIdToName(shard);
            
            // For debugging
            console.log(`Shard ${index}:`, shard, 'Mapped to:', mappedShardName, 'URL:', imageUrl);
            
            return (
                <div key={`shard-${index}`} className="sr-shard-icon">
                    <img 
                        src={imageUrl} 
                        alt={
                            !isNaN(numericShardId) ? 
                            sharedRuneIdMapping[numericShardId]?.name || mappedShardName : 
                            mappedShardName
                        } 
                    />
                </div>
            );
        });
    };

    const fullPrimaryRunes = [runePage.primaryPath, ...runePage.primaryRunes];
    const fullSecondaryRunes = [runePage.secondaryPath, ...runePage.secondaryRunes];

    return (
        <div className="sr-simple-rune-display">
            <div className="sr-runes-wrapper">
                <div className="sr-rune-trees">
                    <div className="sr-primary-tree">{renderTreeImage(runePage.primaryPath)}</div>
                    <div className="sr-secondary-tree">{renderTreeImage(runePage.secondaryPath)}</div>
                </div>
                <div className="sr-primary-rune-column">
                    <div className="sr-runes">{renderRunes(fullPrimaryRunes)}</div>
                </div>
                <div className="sr-secondary-rune-column">
                    <div className="sr-runes">{renderRunes(fullSecondaryRunes)}</div>
                </div>
                <div className="sr-shards-column">
                    {renderShards()}
                </div>
            </div>
        </div>
    );
};

export default SimpleRuneDisplay;
